
import React, { useState, useMemo } from 'react';
import { Article } from '../types.ts';

const ArticleDetails: React.FC<{article: Article}> = ({article}) => (
    <div className="bg-gray-100 p-3 mt-2 rounded-md text-xs text-gray-700">
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            <span><strong>Elasticità:</strong> {article.details.elasticity}</span>
            <span><strong>Disegno:</strong> {article.details.drawing}</span>
            <span><strong>Ordito:</strong> {article.details.warp}</span>
            <span><strong>Trama:</strong> {article.details.weft}</span>
            <span><strong>Colore:</strong> {article.details.color}</span>
            <span><strong>Finissaggio:</strong> {article.details.finishing}</span>
        </div>
    </div>
);

const ArticleItem: React.FC<{ 
  article: Article, 
  isInCollection: boolean, 
  isSelected: boolean, 
  onToggleSelect: (id: string) => void,
  onInspect: (id: string) => void,
  isInspected: boolean,
}> = ({ article, isInCollection, isSelected, onToggleSelect, onInspect, isInspected }) => {
  const statusClass = article.status === 'obsolete' ? 'text-red-500 font-semibold' : '';
  const managedClass = !article.managed ? 'text-blue-500' : '';
  const inCollectionClass = isInCollection ? 'text-green-700' : '';

  return (
    <li className={`p-3 border my-2 transition-all duration-200 rounded-lg shadow-sm ${isInspected ? 'bg-sky-50 border-sky-300' : 'bg-white border-gray-200'}`}>
      <div className="flex items-center gap-3 cursor-pointer" onClick={() => onInspect(article.id)}>
        <input 
          type="checkbox"
          checked={isSelected}
          onClick={(e) => e.stopPropagation()} // Prevents li onClick
          onChange={() => onToggleSelect(article.id)}
          className="form-checkbox h-5 w-5 text-sky-600 rounded focus:ring-sky-500 transition duration-150 ease-in-out"
          aria-label={`Seleziona articolo ${article.name}`}
        />
        <div className={`flex-grow ${inCollectionClass}`}>
          <div className="flex justify-between items-center">
            <span className={`font-medium ${statusClass} ${managedClass}`}>{article.name}</span>
            {isInCollection && <i className="fas fa-check-circle text-green-600" title="Nella collezione"></i>}
          </div>
          <p className="text-xs text-gray-500">{article.code}</p>
        </div>
        <i className={`fas fa-chevron-down text-gray-400 transition-transform duration-200 ${isInspected ? 'rotate-180' : ''}`}></i>
      </div>
      {isInspected && <ArticleDetails article={article} />}
    </li>
  );
};


const ArticlePanel: React.FC<{
  allArticles: Article[];
  collectionArticles: Article[];
  onAdd: (articles: Article[]) => void;
  onInspect: (id: string) => void;
  inspectedArticleId: string | null;
}> = ({ allArticles, collectionArticles, onAdd, onInspect, inspectedArticleId }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState({ color: '', finishing: '', elasticity: '' });
  
  const collectionArticleIds = useMemo(() => new Set(collectionArticles.map(a => a.id)), [collectionArticles]);

  const { uniqueColors, uniqueFinishings, uniqueElasticities } = useMemo(() => {
    const colors = new Set<string>();
    const finishings = new Set<string>();
    const elasticities = new Set<string>();
    allArticles.forEach(a => {
        colors.add(a.details.color);
        finishings.add(a.details.finishing);
        elasticities.add(a.details.elasticity);
    });
    return { 
        uniqueColors: Array.from(colors).sort(),
        uniqueFinishings: Array.from(finishings).sort(),
        uniqueElasticities: Array.from(elasticities).sort(),
    };
  }, [allArticles]);

  const filteredArticles = useMemo(() => {
    return allArticles.filter(a => {
        const searchMatch = !searchTerm || a.name.toLowerCase().includes(searchTerm.toLowerCase()) || a.code.toLowerCase().includes(searchTerm.toLowerCase());
        const colorMatch = !filters.color || a.details.color === filters.color;
        const finishingMatch = !filters.finishing || a.details.finishing === filters.finishing;
        const elasticityMatch = !filters.elasticity || a.details.elasticity === filters.elasticity;
        return searchMatch && colorMatch && finishingMatch && elasticityMatch;
    });
  }, [allArticles, searchTerm, filters]);

  const handleToggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleAddClick = () => {
    const articlesToAdd = allArticles.filter(a => selectedIds.has(a.id));
    onAdd(articlesToAdd);
    setSelectedIds(new Set());
  };
  
  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({...prev, [name]: value}));
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-lg flex flex-col h-full">
      <h2 className="text-xl font-bold mb-4 text-gray-800 border-b pb-2">Tutti gli Articoli</h2>
      <div className="relative mb-2">
        <input 
          type="text"
          placeholder="Cerca per nome o codice..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="w-full p-2 pl-8 border border-gray-300 rounded-md shadow-sm bg-white"
        />
        <i className="fas fa-search absolute left-2.5 top-3 text-gray-400"></i>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
          <select name="color" value={filters.color} onChange={handleFilterChange} className="w-full p-2 border border-gray-300 rounded-md text-sm bg-white">
            <option value="">Tutti i Colori</option>
            {uniqueColors.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select name="finishing" value={filters.finishing} onChange={handleFilterChange} className="w-full p-2 border border-gray-300 rounded-md text-sm bg-white">
            <option value="">Tutti i Finissaggi</option>
            {uniqueFinishings.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
          <select name="elasticity" value={filters.elasticity} onChange={handleFilterChange} className="w-full p-2 border border-gray-300 rounded-md text-sm bg-white">
            <option value="">Tutte le Elasticità</option>
            {uniqueElasticities.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
      </div>
      <p className="text-sm text-gray-500 mb-2">Trovati: {filteredArticles.length}</p>
      <ul className="flex-grow overflow-y-auto pr-1 -mr-2">
        {filteredArticles.map(article => (
          <ArticleItem 
            key={article.id}
            article={article}
            isInCollection={collectionArticleIds.has(article.id)}
            isSelected={selectedIds.has(article.id)}
            onToggleSelect={handleToggleSelect}
            onInspect={onInspect}
            isInspected={article.id === inspectedArticleId}
          />
        ))}
      </ul>
       <button 
          onClick={handleAddClick} 
          disabled={selectedIds.size === 0}
          className="mt-4 w-full bg-sky-600 text-white p-2 rounded-md hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-md"
        >
          Aggiungi alla Collezione ({selectedIds.size})
        </button>
    </div>
  );
};

export default ArticlePanel;